import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as dropboxService from '@app/services/dropbox';
import {
  setFolders,
  addFolder,
  updateFolder,
  removeFolder,
  setLoading,
  setError,
} from '@app/store/reducers/dropbox';

/**
 * Custom hook for folder operations
 */
export const useDropboxFolders = () => {
  const dispatch = useDispatch();
  const [loading, setLocalLoading] = useState(false);

  // Fetch all folders
  const fetchFolders = useCallback(async (params?: {
    parent_id?: string;
    include_deleted?: boolean;
    global_only?: boolean;
    personal_only?: boolean;
  }) => {
    try {
      dispatch(setLoading(true));
      const response = await dropboxService.getFolders(params);
      
      if (response?.success) {
        dispatch(setFolders(response.data));
        return response.data;
      } else {
        const errorMsg = response?.message || 'Failed to fetch folders';
        dispatch(setError(errorMsg));
        toast.error(errorMsg);
        return null;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'An error occurred while fetching folders';
      dispatch(setError(errorMsg));
      toast.error(errorMsg);
      return null;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  // Create new folder
  const createNewFolder = useCallback(
    async (name: string, parentId: string | null = null, isGlobal: boolean = false) => {
      try {
        setLocalLoading(true);
        const response = await dropboxService.createFolder({
          name,
          parent_id: parentId,
          is_global: isGlobal,
        });

        if (response?.success) {
          dispatch(addFolder(response.data));
          toast.success('Folder created successfully');
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to create folder';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while creating folder';
        toast.error(errorMsg);
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  // Rename folder
  const renameFolder = useCallback(
    async (folderId: string, newName: string) => {
      try {
        setLocalLoading(true);
        const response = await dropboxService.updateFolder(folderId, { name: newName });

        if (response?.success) {
          dispatch(updateFolder(response.data));
          toast.success('Folder renamed successfully');
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to rename folder';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while renaming folder';
        toast.error(errorMsg);
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  // Delete folder
  const deleteFolder = useCallback(
    async (folderId: string, permanent: boolean = false) => {
      try {
        setLocalLoading(true);
        const response = await dropboxService.deleteFolder(folderId, permanent);

        if (response?.success) {
          if (permanent) {
            dispatch(removeFolder(folderId));
            toast.success('Folder permanently deleted');
          } else {
            dispatch(updateFolder(response.data));
            toast.success('Folder moved to trash');
          }
          return true;
        } else {
          const errorMsg = response?.message || 'Failed to delete folder';
          toast.error(errorMsg);
          return false;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while deleting folder';
        toast.error(errorMsg);
        return false;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  // Restore folder
  const restoreFolder = useCallback(
    async (folderId: string) => {
      try {
        setLocalLoading(true);
        const response = await dropboxService.restoreFolder(folderId);

        if (response?.success) {
          dispatch(updateFolder(response.data));
          toast.success('Folder restored successfully');
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to restore folder';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while restoring folder';
        toast.error(errorMsg);
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  // Move folder
  const moveFolderTo = useCallback(
    async (folderId: string, newParentId: string | null) => {
      try {
        setLocalLoading(true);
        const response = await dropboxService.moveFolder(folderId, newParentId);

        if (response?.success) {
          dispatch(updateFolder(response.data));
          toast.success('Folder moved successfully');
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to move folder';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while moving folder';
        toast.error(errorMsg);
        return null;
      } finally {
        setLocalLoading(false);
      }
    },
    [dispatch]
  );

  // Reorder folder
  const reorderFolder = useCallback(
    async (folderId: string, sortOrder: number) => {
      try {
        const response = await dropboxService.reorderFolder(folderId, sortOrder);

        if (response?.success) {
          dispatch(updateFolder(response.data));
          return response.data;
        } else {
          const errorMsg = response?.message || 'Failed to reorder folder';
          toast.error(errorMsg);
          return null;
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'An error occurred while reordering folder';
        toast.error(errorMsg);
        return null;
      }
    },
    [dispatch]
  );

  return {
    loading,
    fetchFolders,
    createNewFolder,
    renameFolder,
    deleteFolder,
    restoreFolder,
    moveFolderTo,
    reorderFolder,
  };
};
